import { useMutation, useQuery, useQueryClient } from 'react-query';
import { client } from 'src/utils/api-client';
import {
  AnsweredQuestionsSchema,
  IndicatorResponsibility,
  IndicatorSchema,
} from './types';
import { getOrganizationReports } from './api';
import { toast } from 'react-toastify';
import { useEsgOrganization, useLanguage } from 'src/common/hooks';
import { useTranslation } from 'react-i18next';
import {
  EsgStakeholderDataInSchema,
  EsgStakeholderDataOutSchema,
  EsgStakeholdersTypeSchema,
} from './Dma/Stakeholders/SelectingStakeholders/SelectingStakeholdersTable/index.types';
import { EsgReportInSchema } from './api.types';

export const useIndicators = (esgReportId: number | null | undefined) => {
  const { language } = useLanguage();
  const url = '/web/esg-administration-panel';
  const params = { esg_report_id: esgReportId };

  const { data: indicators } = useQuery(
    ['indicators', esgReportId, language],
    () =>
      client
        .get<IndicatorSchema[]>(`${url}/indicators`, { params })
        .then((res) => res.data),
    { enabled: !!esgReportId }
  );

  const { data: answeredQuestions } = useQuery(
    ['answered-esg-questions', esgReportId],
    () =>
      client
        .get<AnsweredQuestionsSchema>(`${url}/answered-esg-questions`, {
          params,
        })
        .then((res) => res.data),
    { enabled: !!esgReportId }
  );

  return {
    indicators: indicators || [],
    answeredQuestions: answeredQuestions || {},
  };
};

export const useReports = (orgId: number | null = null) => {
  const { id: activeOrganizationId } = useEsgOrganization();
  const organizationId = orgId || activeOrganizationId;
  const queryKey = ['esg-reports', { organizationId }];
  const queryClient = useQueryClient();

  const query = useQuery(queryKey, () =>
    getOrganizationReports({
      organizationId: organizationId || activeOrganizationId,
    })
  );

  const create = useMutation({
    mutationFn: (data: EsgReportInSchema) =>
      client.post(`web/esg_v2/${organizationId}`, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return { reports: query.data || [], ...query, create };
};

export const useIndicatorsResponsibilites = (
  esgReportId: number | null | undefined
) => {
  const queryKey = ['indicators-responsibilities', { esgReportId }];
  const url = '/web/esg-administration-panel/indicators-responsibilities';
  const queryClient = useQueryClient();
  const invalidateQuery = () => queryClient.invalidateQueries(queryKey);
  const { t } = useTranslation(undefined, {
    keyPrefix: 'esg.administrationPanel.toasts',
  });
  const params = { esg_report_id: esgReportId };

  const { data } = useQuery(
    queryKey,
    () =>
      client
        .get<IndicatorResponsibility[]>(url, { params })
        .then((res) => res.data),
    { enabled: !!esgReportId }
  );

  const createIndicatorResponsibility = useMutation({
    mutationFn: (data: Omit<IndicatorResponsibility, 'id'>) =>
      client.post(url, data),
    onSuccess: () => {
      invalidateQuery();
      toast.success(t('responsibilityCreated') as string);
    },
  });

  const updateIndicatorResponsibility = useMutation({
    mutationFn: (data: IndicatorResponsibility) =>
      client.put(`${url}/${data.id}`, data),
    onSuccess: () => {
      invalidateQuery();
      toast.success(t('responsibilityUpdated') as string);
    },
  });

  const deleteIndicatorResponsibility = useMutation({
    mutationFn: (id: number) => client.delete(`${url}/${id}`),
    onSuccess: () => {
      invalidateQuery();
      toast.success(t('responsibilityDeleted') as string);
    },
  });

  const notify = useMutation({
    mutationFn: (id: number) => client.post(`${url}/${id}/notify`),
  });

  return {
    data: data || [],
    create: createIndicatorResponsibility,
    update: updateIndicatorResponsibility,
    delete: deleteIndicatorResponsibility,
    notify,
  };
};

export const useEsgStakeholderData = (reportId: number) => {
  const url = `/web/esg/v3/stakeholders/data`;
  const queryClient = useQueryClient();
  const queryKey = 'esgStakeholders';
  const params = { esg_report_id: reportId };

  const { data } = useQuery(
    queryKey,
    () =>
      client
        .get<EsgStakeholderDataOutSchema[]>(url, { params })
        .then((res) => res.data),
    { enabled: !!reportId }
  );

  const update = useMutation({
    mutationFn: (data: Partial<EsgStakeholderDataOutSchema>) =>
      client.put(`${url}/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Success');
    },
  });

  const create = useMutation({
    mutationFn: (data: Partial<EsgStakeholderDataInSchema>) =>
      client.post(url, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Success');
    },
  });

  const _delete = useMutation({
    mutationFn: (id: number | null) => client.delete(`${url}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Success');
    },
  });

  return {
    data: data || [],
    update,
    delete: _delete,
    create,
  };
};

export const useEsgStakeholderTypes = () => {
  const url = `/web/esg/v3/stakeholders/types`;
  const queryKey = ['esg', 'stakeholder-types'];
  const query = useQuery(queryKey, () =>
    client.get<EsgStakeholdersTypeSchema[]>(url).then((res) => res.data)
  );

  const data = query.data || [];

  const dataMap = data.reduce(
    (
      acc: Record<number, EsgStakeholdersTypeSchema>,
      item: EsgStakeholdersTypeSchema
    ) => {
      acc[item.id] = item;
      return acc;
    },
    {}
  );

  return { data, dataMap };
};
